/**
 * Continuous client-action capture — the feed behind the admin session timeline (RUM-style, WITHOUT DOM
 * recording). Buffers lightweight user actions and ships them in small batches to
 * `common.logClientActions`, where each becomes a `client_action` wide event correlated by the same
 * trace.id/session.id headers as every other call (see lib/client-trace). Ported from medicos.
 *
 * Captured: route navigations (pathname only) and clicks on interactive controls (by accessible label).
 * NEVER: keystrokes, input values, or DOM content beyond the clicked control's label. Best-effort — a
 * lost batch is just a gap in the timeline; never throws.
 */
import { correlationHeaders } from './client-trace'
import { registerClickSink } from './user-action-capture'
import { basename } from './utils'

interface PendingAction {
  t: number
  type: 'nav' | 'click'
  label: string
  path: string
}

const MAX_BUFFER = 50 // matches the server schema cap; oldest dropped first
const FLUSH_INTERVAL_MS = 10_000
const FLUSH_AT = 20

let buffer: PendingAction[] = []

function push(action: PendingAction): void {
  buffer.push(action)
  if (buffer.length > MAX_BUFFER) buffer = buffer.slice(-MAX_BUFFER)
  if (buffer.length >= FLUSH_AT) flush()
}

// Fire-and-forget POST straight to the tRPC endpoint (not the tRPC client) so `keepalive` lets the send
// finish even when it races a navigation/tab close — exactly when a timeline's last actions matter. The
// correlation headers are the shared correlationHeaders() main.tsx's headers() also uses; cookies ride
// along same-origin. A failed batch is dropped.
function flush(): void {
  if (buffer.length === 0) return
  const actions = buffer
  buffer = []
  try {
    const headers: Record<string, string> = {
      'content-type': 'application/json',
      ...correlationHeaders({ includeParentSpan: true }),
    }
    // Same `${basename}/trpc` endpoint main.tsx targets — the app can be mounted under /danchiano.
    void fetch(`${basename}/trpc/common.logClientActions?batch=1`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ 0: { actions } }),
      keepalive: true,
    }).catch(() => {
      // Swallow — a lost batch is acceptable.
    })
  } catch {
    // fetch/keepalive can throw on exotic browsers; same rule.
  }
}

/** Record a resolved navigation (pathname only — query strings can carry tokens/ids). */
export function trackNavigation(href: string): void {
  const path = href.split('?')[0] ?? href
  push({ t: Date.now(), type: 'nav', label: '', path: path.slice(0, 512) })
}

/**
 * Install passive capture: a click sink on the shared capture pipeline (lib/user-action-capture), a
 * periodic flush, and a last-chance flush on tab hide / pagehide. Call once on the client (router.tsx
 * only calls this when the wide-events flag is on — the sink is never registered otherwise); no-op on
 * the server.
 */
export function installClientActionCapture(): void {
  if (typeof window === 'undefined') return

  // The document load is the session's entry point (every hard reload a re-entry). The router's
  // onResolved only fires for SPA transitions, so record the initial location here.
  push({ t: Date.now(), type: 'nav', label: '', path: window.location.pathname })

  registerClickSink((label) => {
    push({ t: Date.now(), type: 'click', label, path: window.location.pathname })
  })

  window.setInterval(flush, FLUSH_INTERVAL_MS)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush()
  })
  window.addEventListener('pagehide', flush)
}
